/**
 * The grid of leader portraits (photo, name, job title), each linking to that leader's profile at
 * `/about-us/leadership-team/<slug>`, with an optional last tile that links elsewhere.
 *
 * Position: the renderer of the Contentful leader list. Used by
 * `components/Leadership/LeadershipSection.tsx` on `/about-us/leadership-team` (no action tile) and
 * by `components/AboutUs/LeadershipTeam/LeadershipTeam.tsx` on `/about-us` (action tile "View The
 * Entire Leadership Team" → `/about-us/leadership-team`, from `content/ryerson/about-us/index.ts`
 * → `LeadershipTeam`). These two are its only importers. The list comes from
 * `src/contentful/leader/getContentfulLeaders.ts` at build time, already sorted by the entry's
 * `order` field; this component keeps that order.
 *
 * Rules a change must keep:
 * - The profile link `"/about-us/leadership-team/" + slug` must match the page route
 *   `src/pages/about-us/leadership-team/[leaderSlug].tsx`, whose paths come from
 *   `src/contentful/leader/getContentfulLeadersSlugs.ts`. A slug outside that list is a 404.
 * - Photos are requested from the Contentful Images API as 260 × 360 JPEGs
 *   (`?fm=jpg&fit=fill&w=260&h=360`) on both layouts.
 *
 * Desktop (`lg` and up) shows four portraits per row, mobile two. The action tile's height is kept
 * in state so it matches the portraits' height (see the effect in {@link ContentfulLeaders}).
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import { Link } from "@ryerson/frontend.navigation";
import { Icon } from "@ryerson/frontend.assets";
import { Typography } from "@ryerson/frontend.typography";
import { ProcessedContentfulLeadersFieldsByLanguage } from "@contentful/leader/types";

/**
 * A Contentful asset file, kept from the Gatsby-era version of this file (then `Team/Team.tsx`).
 * Nothing imports it since the Next.js migration; `components/Products/Types/Types.tsx` has its
 * own `ContentfulFile`. The live leader shape is `src/contentful/leader/types/index.ts`.
 */
export type ContentfulFile = {
	url: string;
};

/** A Contentful asset with its file. Unused, like {@link ContentfulFile}. */
export type ContentfulMedia = {
	file: ContentfulFile;
};

const Portraits = styled.div`
	display: block;
	width: 100%;
	height: auto;
	margin-top: 120px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				margin-top: 45px;
			}
		`;
	}}
`;

const Portrait = styled.div`
	display: inline-block;
	width: calc(25% - 30px);
	height: auto;
	vertical-align: top;
	margin-bottom: 45px;
	img {
		width: 100%;
		height: auto;
		display: block;
		margin-bottom: 30px;
	}
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				width: calc(50% - 10px);
				box-sizing: border-box;
				img {
					margin-bottom: 15px;
					border: 1px solid ${theme.colors.primary.lighterGray};
				}
			}
		`;
	}}
`;

const Action = styled.div`
	display: inline-block;
	width: calc(25% - 30px);
	height: auto;
	vertical-align: top;
	margin-bottom: 45px;
	position: relative;
	padding: 40px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				width: calc(50% - 10px);
				padding-top: 50%;
				box-sizing: border-box;
			}
		`;
	}}
`;

const ActionIcon = styled.div`
	display: block;
	position: absolute;
	bottom: 40px;
	right: 40px;
	width: 40px;
	height: 40px;
	border-radius: 40px;
	text-align: center;
	line-height: 40px;
	cursor: pointer;
	box-sizing: border-box;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				width: 24px;
				height: 24px;
				right: unset;
				bottom: unset;
				top: 20px;
				left: 20px;
				line-height: 24px;
				border-radius: 24px;
			}
		`;
	}}
`;

/** Props of {@link ContentfulLeaders}. */
export type ContentfulLeadersProps = {
	/**
	 * Target of the last tile. The tile is drawn only when both this and `actionLabel` are
	 * non-empty.
	 */
	actionUrl?: string;
	/** Text of the last tile. */
	actionLabel?: string;
	/** Leader cards per language; the entry for the current language is rendered. */
	contentfulLeadershipTeam: ProcessedContentfulLeadersFieldsByLanguage;
};

/**
 * Renders the current language's leaders as linked portraits, then the action tile when
 * `actionUrl` and `actionLabel` are both set. On desktop the whole action tile is the link; on
 * mobile only its chevron icon is.
 */
const ContentfulLeaders: React.FC<ContentfulLeadersProps> = ({
	actionUrl,
	actionLabel,
	contentfulLeadershipTeam,
}) => {
	const { theme } = useTheme();

	const {
		localization: { language },
	} = useApplication();

	const leaders = contentfulLeadershipTeam[language];

	// Height in px of the action tile, so it lines up with the portraits. At a viewport of 1160px
	// or more the desktop height is 360px. Below that each height is an estimated portrait width
	// (viewport − 200px over four columns for desktop, viewport − 60px over two for mobile)
	// divided by 0.7225, which is within 0.001 of the 260/360 width-to-height ratio of the
	// requested photos. These numbers are literals here, not read from the theme or the layout,
	// so a change to the portrait width or the container padding leaves the tile out of line.
	// The same calculation runs on mount and on every resize; the listener is not removed on
	// unmount.
	const [desktopHeight, setDesktopHeight] = React.useState<number>(360);
	const [mobileHeight, setMobileHeight] = React.useState<number>(360);
	React.useEffect(() => {
		if (window) {
			window.addEventListener("resize", () => {
				if (window.innerWidth >= 1160) setDesktopHeight(360);
				else {
					let imageWidth = (window.innerWidth - 200) / 4;
					let mobileImageWidth = (window.innerWidth - 60) / 2;
					let aspect = 0.7225;
					let height = imageWidth / aspect;
					let mobileHeight = mobileImageWidth / aspect;
					setMobileHeight(mobileHeight);
					setDesktopHeight(height);
				}
			});
			if (window.innerWidth >= 1160) setDesktopHeight(360);
			else {
				let imageWidth = (window.innerWidth - 200) / 4;
				let mobileImageWidth = (window.innerWidth - 60) / 2;
				let aspect = 0.7225;
				let height = imageWidth / aspect;
				let mobileHeight = mobileImageWidth / aspect;
				setMobileHeight(mobileHeight);
				setDesktopHeight(height);
			}
		}
	}, []);

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<Portraits theme={theme}>
					{/* Four per row: every fourth portrait drops its right margin. */}
					{leaders.map((entry, index) => (
						<React.Fragment key={entry.slug}>
							<Link to={"/about-us/leadership-team/" + entry.slug}>
								<Portrait
									theme={theme}
									css={css`
										margin-right: ${(index + 1) % 4 === 0 ? "0px" : "40px"};
									`}
								>
									<img
										src={
											entry.picture.file.url + "?fm=jpg&fit=fill&w=260&h=360"
										}
										alt={entry.fullName}
									/>
									<Typography
										css={css`
											font-size: 20px;
											margin-top: 0px;
											margin-bottom: 8px;
										`}
										variant="h4"
										color={theme.colors.primary.primaryBrand}
									>
										{entry.fullName}
									</Typography>
									<Typography
										css={css`
											white-space: pre-line;
										`}
										color={theme.colors.primary.gray}
										size="sm"
									>
										{entry.title}
									</Typography>
								</Portrait>
							</Link>
						</React.Fragment>
					))}
					{actionLabel && actionLabel.length > 0 && actionUrl && actionUrl.length > 0 ? (
						<Link to={actionUrl}>
							<Action
								theme={theme}
								css={css`
									background-color: ${theme.colors.tertiary.background};
									height: ${desktopHeight + "px"};
								`}
							>
								<Typography color={theme.colors.primary.white} size="sm">
									{actionLabel}
								</Typography>

								<ActionIcon
									theme={theme}
									css={css`
										background-color: ${theme.colors.primary.primaryBrand};
									`}
								>
									<Icon
										icon="chevron-right"
										size="xs"
										color={theme.colors.primary.white}
										circled={false}
										css={css`
											cursor: pointer;
										`}
									/>
								</ActionIcon>
							</Action>
						</Link>
					) : (
						<></>
					)}
				</Portraits>
			</Media>
			<Media lessThan="lg">
				<Portraits theme={theme}>
					{/* Two per row: every second portrait drops its right margin. */}
					{leaders.map((entry, index) => (
						<React.Fragment key={index}>
							<Link to={"/about-us/leadership-team/" + entry.slug}>
								<Portrait
									key={index}
									theme={theme}
									css={css`
										margin-right: ${(index + 1) % 2 === 0 ? "0px" : "20px"};
									`}
								>
									<img
										src={
											entry.picture.file.url + "?fm=jpg&fit=fill&w=260&h=360"
										}
										alt={entry.fullName}
									/>
									<Typography
										css={css`
											margin-top: 0px;
											margin-bottom: 0px;
										`}
										variant="h5"
										color={theme.colors.primary.primaryBrand}
									>
										{entry.fullName}
									</Typography>
									<Typography
										css={css`
											white-space: pre-line;
										`}
										type="secondary"
										size="md"
									>
										{entry.title}
									</Typography>
								</Portrait>
							</Link>
						</React.Fragment>
					))}
					{/* On mobile the label is not inside the link; only the chevron navigates. */}
					{actionLabel && actionLabel.length > 0 && actionUrl && actionUrl.length > 0 ? (
						<Action
							theme={theme}
							css={css`
								background-color: ${theme.colors.tertiary.background};
								height: ${mobileHeight + "px"};
							`}
						>
							<Typography
								css={css`
									position: absolute;
									bottom: 20px;
									left: 20px;
									padding-right: 40px;
									box-sizing: border-box;
									display: block;
									width: 100%;
								`}
								color={theme.colors.primary.white}
								weight="bold"
								size="md"
							>
								{actionLabel}
							</Typography>
							<Link to={actionUrl}>
								<ActionIcon
									theme={theme}
									css={css`
										background-color: ${theme.colors.primary.primaryBrand};
									`}
								>
									<Icon
										icon="chevron-right"
										size="xs"
										color={theme.colors.primary.white}
										circled={false}
										css={css`
											cursor: pointer;
										`}
									/>
								</ActionIcon>
							</Link>
						</Action>
					) : (
						<></>
					)}
				</Portraits>
			</Media>
		</>
	);
};

export default ContentfulLeaders;
